/**
 * Russian date/number formatting for the UI copy in the design spec.
 *
 * `Intl` handles most of it, but the spec uses two grammatical cases that
 * `Intl` will not produce: "12 июля" (genitive, which `day+month` does give)
 * and "применяли в марте 2026" (prepositional, which it does not — `month:
 * 'long'` alone returns the nominative "март"). Hence the small table below.
 */

const PREPOSITIONAL_MONTHS = [
  "январе",
  "феврале",
  "марте",
  "апреле",
  "мае",
  "июне",
  "июле",
  "августе",
  "сентябре",
  "октябре",
  "ноябре",
  "декабре",
] as const;

const dayMonth = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });
const dayMonthYear = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
});
const timeOnly = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const weekdayLong = new Intl.DateTimeFormat("ru-RU", { weekday: "long" });

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Midnight-anchored day difference: "today" must not depend on the clock. */
export function daysUntil(target: Date, now: Date = new Date()): number {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a.getTime() - b.getTime()) / 86_400_000);
}

/** "12 июля", and "12 июля 2025" once the year stops being the current one. */
export function formatDayMonth(date: Date, now: Date = new Date()): string {
  return date.getFullYear() === now.getFullYear()
    ? dayMonth.format(date)
    : dayMonthYear.format(date);
}

/** "8 июля, 18:20" — used for completion stamps (§8). */
export function formatDateTime(date: Date, now: Date = new Date()): string {
  return `${formatDayMonth(date, now)}, ${timeOnly.format(date)}`;
}

/** "в марте 2026" — the "applied at" line on memory cards (§8). */
export function formatMonthYearPrepositional(date: Date): string {
  return `в ${PREPOSITIONAL_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Compact relative stamp for feed metadata: "12:04", "вчера", "12 июля". */
export function formatRelative(date: Date, now: Date = new Date()): string {
  if (sameDay(date, now)) return timeOnly.format(date);
  const diff = daysUntil(date, now);
  if (diff === -1) return "вчера";
  if (diff > -7 && diff < 0) return weekdayLong.format(date);
  return formatDayMonth(date, now);
}

/** Capitalised weekday + date for the Today / Dashboard headers. */
export function formatFullDate(date: Date): string {
  const weekday = weekdayLong.format(date);
  return `${weekday[0].toUpperCase()}${weekday.slice(1)}, ${dayMonth.format(date)}`;
}

/** Russian plurals: pluralRu(1,"ошибка","ошибки","ошибок") → "ошибка". */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/**
 * Coarse "how long ago" for freshness lines: "2 недели назад", "полгода назад".
 *
 * Deliberately vague where `formatRelative` is exact. On a suggestion card the
 * question is "is this still how we do things", and the honest answer has a
 * resolution of weeks — a precise date would invite a precision the ranking
 * itself does not have (it penalises age in 90-day steps).
 */
export function formatRelativePast(date: Date, now: Date = new Date()): string {
  const days = Math.max(0, -daysUntil(date, now));
  if (days === 0) return "сегодня";
  if (days === 1) return "вчера";
  if (days < 7) return `${days} ${pluralRu(days, "день", "дня", "дней")} назад`;
  if (days < 31) {
    const weeks = Math.round(days / 7);
    return `${weeks} ${pluralRu(weeks, "неделю", "недели", "недель")} назад`;
  }
  const months = Math.round(days / 30);
  if (months < 12) return `${months} ${pluralRu(months, "месяц", "месяца", "месяцев")} назад`;
  const years = Math.round(days / 365);
  return `${years} ${pluralRu(years, "год", "года", "лет")} назад`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} МБ`;
}
